import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

export interface DetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  /** Side of the screen. Defaults to right. */
  side?: "right" | "left" | "bottom";
  /** Width class for right/left side. */
  widthClassName?: string;
  /** Optional sticky footer (action bar). */
  footer?: ReactNode;
  children: ReactNode;
}

export function DetailDrawer({
  open,
  onOpenChange,
  title,
  description,
  side = "right",
  widthClassName = "sm:max-w-xl md:max-w-2xl",
  footer,
  children,
}: DetailDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={side}
        className={cn(
          "flex w-full flex-col gap-0 p-0",
          side === "right" || side === "left" ? widthClassName : "",
        )}
      >
        <SheetHeader className="border-b border-border px-6 py-4 text-left">
          <SheetTitle className="text-base font-semibold">{title}</SheetTitle>
          {description ? (
            <SheetDescription className="text-xs text-muted-foreground">
              {description}
            </SheetDescription>
          ) : (
            <SheetDescription className="sr-only">Detail panel</SheetDescription>
          )}
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
        {footer && (
          <div className="border-t border-border bg-background/95 px-6 py-3 backdrop-blur">
            {footer}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
